import { useEffect, useState, useCallback, useRef } from "react";
import { Plus, Search, Loader2, Trash2, Pencil, Package, Download, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";

const rupee = (n) => `\u20B9${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const blank = { item_name: "", hsn: "", description: "", rate: "", quantity: "", gst_inclusive: false };

export default function ItemMasters() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const fileRef = useRef(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/item-masters", { params });
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { const t = setTimeout(fetchItems, 250); return () => clearTimeout(t); }, [fetchItems]);

  const openNew = () => { setForm(blank); setEditingId(null); setOpen(true); };
  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({
      item_name: it.item_name || "", hsn: it.hsn || "", description: it.description || "",
      rate: it.rate ?? "", quantity: it.quantity ?? "", gst_inclusive: !!it.gst_inclusive,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.item_name.trim()) { toast.error("Item name is required"); return; }
    setSaving(true);
    const payload = {
      item_name: form.item_name.trim(), hsn: form.hsn.trim(), description: form.description.trim(),
      rate: Number(form.rate || 0), quantity: Number(form.quantity || 0), gst_inclusive: !!form.gst_inclusive,
    };
    try {
      if (editingId) {
        const { data } = await api.put(`/item-masters/${editingId}`, payload);
        setItems((p) => p.map((x) => (x.id === editingId ? data : x)));
        toast.success("Item updated");
      } else {
        const { data } = await api.post("/item-masters", payload);
        setItems((p) => [data, ...p]);
        toast.success("Item saved");
      }
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    const id = deleteId; const prev = items;
    setItems((p) => p.filter((x) => x.id !== id));
    setDeleteId(null);
    try { await api.delete(`/item-masters/${id}`); toast.success("Item deleted"); }
    catch (e) { setItems(prev); toast.error(formatApiError(e)); }
  };

  const downloadCsv = (path, filename, okMsg) => {
    const token = localStorage.getItem("access_token");
    fetch(`${API}/${path}`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => { if (!r.ok) throw new Error("Download failed"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
        if (okMsg) toast.success(okMsg);
      })
      .catch(() => toast.error("Download failed"));
  };

  const handleImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/imports/item-masters.csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported}, skipped ${data.skipped}`);
      fetchItems();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="space-y-6" data-testid="item-masters-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B] mb-1">Module 08</div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">Item Masters cum Stock</h1>
          <p className="text-sm text-[#475569] mt-1">Save reusable items with HSN, rate &amp; stock quantity — then pick them instantly while building quotations &amp; invoices.</p>
        </div>
        <Button onClick={openNew} className="bg-[#0055FF] hover:bg-[#0040C1] text-white gap-2" data-testid="add-item-master-btn">
          <Plus className="w-4 h-4" /> New item
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <Input placeholder="Search by name, HSN, description…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10" data-testid="item-masters-search" />
        </div>
        <Button variant="outline" onClick={() => downloadCsv("exports/item-masters-template.csv", "item-masters-template.csv", "Template downloaded · fill it in and use Import")} className="h-10 gap-2" data-testid="item-masters-template-btn">
          <Download className="w-4 h-4" /> Template
        </Button>
        <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleImport} data-testid="item-masters-import-input" />
        <Button variant="outline" onClick={() => fileRef.current?.click()} className="h-10 gap-2" data-testid="item-masters-import-btn">
          <Upload className="w-4 h-4" /> Import CSV
        </Button>
        <Button variant="outline" onClick={() => downloadCsv("exports/item-masters.csv", "item-masters.csv")} className="h-10 gap-2" data-testid="item-masters-export-btn">
          <Download className="w-4 h-4" /> Export
        </Button>
      </div>

      <div className="border border-[#E2E8F0] rounded-lg bg-white overflow-x-auto">
        <Table>
          <TableHeader className="bg-[#F8F9FA]">
            <TableRow>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Item Name</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">HSN</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider hidden md:table-cell">Description</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Rate</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-center">Qty / Stock</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-center">GST</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-sm text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-sm text-[#64748B]" data-testid="item-masters-empty">
                No items yet. Add your frequently-quoted items to reuse them later.
              </TableCell></TableRow>
            ) : items.map((it) => (
              <TableRow key={it.id} data-testid={`item-master-row-${it.id}`}>
                <TableCell className="text-sm font-medium">
                  <span className="inline-flex items-center gap-2"><Package className="w-4 h-4 text-[#0055FF]" /> {it.item_name}</span>
                </TableCell>
                <TableCell className="font-mono text-xs text-[#64748B]">{it.hsn || "—"}</TableCell>
                <TableCell className="text-xs text-[#475569] max-w-[320px] truncate hidden md:table-cell">{it.description || "—"}</TableCell>
                <TableCell className="text-right font-mono text-sm">{rupee(it.rate)}</TableCell>
                <TableCell className="text-center font-mono text-sm" data-testid={`item-master-qty-${it.id}`}>{Number(it.quantity || 0).toLocaleString("en-IN")}</TableCell>
                <TableCell className="text-center">
                  <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${it.gst_inclusive ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#EEF2FA] text-[#334155]"}`}>
                    {it.gst_inclusive ? "Incl" : "Excl"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`edit-item-master-${it.id}`}><Pencil className="w-4 h-4 text-[#0055FF]" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(it.id)} data-testid={`delete-item-master-${it.id}`}><Trash2 className="w-4 h-4 text-[#EF4444]" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="item-master-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editingId ? "Edit item" : "New item"}</DialogTitle>
            <DialogDescription>Reusable item for quotations — HSN &amp; GST are optional.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Item Name *</Label>
                <Input value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} placeholder="e.g. Dell Latitude 7490" data-testid="item-master-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">HSN Code</Label>
                <Input value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value })} className="font-mono" placeholder="e.g. 8471" data-testid="item-master-hsn" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-[#475569]">Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Specs / configuration / notes" data-testid="item-master-desc" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Rate (₹)</Label>
                <Input type="number" min="0" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} className="font-mono" placeholder="e.g. 25000" data-testid="item-master-rate" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Quantity / Stock</Label>
                <Input type="number" min="0" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="font-mono" placeholder="e.g. 10" data-testid="item-master-qty" />
              </div>
              <div className="flex items-center gap-3 md:pt-6">
                <Switch checked={form.gst_inclusive} onCheckedChange={(v) => setForm({ ...form, gst_inclusive: v })} data-testid="item-master-incl" />
                <Label className="text-sm">Incl. of GST</Label>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="item-master-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0055FF] hover:bg-[#0040C1] text-white" data-testid="item-master-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? "Save changes" : "Save item")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete item?</AlertDialogTitle>
            <AlertDialogDescription>This removes the saved item. Existing quotations are unaffected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="confirm-item-master-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
